import { Flex, Text, VStack } from '@chakra-ui/react'
import React from 'react'
import Toggle from 'react-toggle'
import { ImageIcon } from '~/components/ImageIcon'
import { themeColorSectionKeys } from '~/themes/colors/colors'
import { textStyleKeys } from '~/themes/textStyles'
import { noop } from '~/utils/functions'

import {
  DependentSectionPanel,
  MenuItemAlertContentWrapper,
  MenuItemHeader,
  MenuItemPrimaryContentWrapper,
  MenuItemWrapper,
  ToggleWrapper,
} from './Shared'

const { subLabelColor, settingItemAlertColor } =
  themeColorSectionKeys.Settings.Shared

interface SettingItemBaseProps {
  label: string
  subLabel?: string
  icon?: SVGAsset | undefined
  onClick: () => void
  controlContent?: React.ReactNode
  alertContent?: React.ReactNode
  disabled?: boolean
}

const SettingItemBase = ({
  label,
  subLabel,
  icon,
  onClick,
  controlContent,
  alertContent,
  disabled,
}: SettingItemBaseProps) => {
  const handleClick = () => {
    if (disabled) {
      return
    }
    onClick()
  }
  const isAlert = alertContent !== undefined

  return (
    <MenuItemWrapper onClick={() => handleClick()} isAlert={isAlert}>
      <MenuItemPrimaryContentWrapper hasAlert={isAlert}>
        <Flex direction={'row'}>
          {icon && (
            <ImageIcon width="24" height="24" marginRight="10" icon={icon} />
          )}
          <Flex direction={'column'} marginRight={8}>
            <Text textStyle={textStyleKeys.Body2}>{label}</Text>
            {subLabel && (
              <Text textStyle={textStyleKeys.Body4} color={subLabelColor}>
                {subLabel}
              </Text>
            )}
          </Flex>
        </Flex>

        {controlContent}
      </MenuItemPrimaryContentWrapper>
      {alertContent && (
        <MenuItemAlertContentWrapper>
          {alertContent}
        </MenuItemAlertContentWrapper>
      )}
    </MenuItemWrapper>
  )
}

export const SettingLinkAction = (props: SettingItemBaseProps) => {
  return <SettingItemBase {...props} />
}

interface SettingToggleProps extends SettingItemBaseProps {
  isChecked: boolean
  alertMessage?: string | undefined
}
export const SettingToggle = (props: SettingToggleProps) => {
  return (
    <SettingItemBase
      {...props}
      controlContent={
        <ToggleWrapper isInHeader={false}>
          <Toggle
            checked={props.isChecked}
            icons={false}
            disabled={props.disabled}
            onChange={noop}
          />
        </ToggleWrapper>
      }
      alertContent={
        props.alertMessage && (
          <Text textStyle={textStyleKeys.Body4} color={settingItemAlertColor}>
            {props.alertMessage}
          </Text>
        )
      }
    />
  )
}

interface SettingToggleDependentSectionProps {
  label: string
  icon?: SVGAsset
  isChecked: boolean
  onClick: () => void
  toggleOnContent: React.ReactNode
  toggleOffContent?: React.ReactNode
}
export const SettingToggleDependentSection = ({
  label,
  icon,
  isChecked,
  onClick: handleClick,
  toggleOnContent,
  toggleOffContent,
}: SettingToggleDependentSectionProps) => {
  const shouldRoundBottomCorners = toggleOffContent === undefined && !isChecked

  return (
    <VStack gap="1rem">
      <MenuItemHeader
        onClick={handleClick}
        roundBottomCorners={shouldRoundBottomCorners}
      >
        {icon && (
          <ImageIcon width="24" height="24" marginRight="10" icon={icon} />
        )}
        <Text textStyle={textStyleKeys.Body2}>{label}</Text>
        <ToggleWrapper isInHeader>
          <Toggle
            checked={isChecked}
            icons={false}
            disabled={false}
            onChange={handleClick}
          />
        </ToggleWrapper>
      </MenuItemHeader>

      {isChecked && (
        <DependentSectionPanel>{toggleOnContent}</DependentSectionPanel>
      )}
      {!isChecked && toggleOffContent != undefined && (
        <DependentSectionPanel>{toggleOffContent}</DependentSectionPanel>
      )}
    </VStack>
  )
}
